import Title from "antd/es/typography/Title";
import { useGetCommissionsQuery } from "api/TenantSlug/Commission/hooks.ts";
import { useGetOfferCustomFields } from "api/TenantSlug/OfferCustomFields/hooks.ts";
import { OfferInformationTable } from "components/Tables/OfferInformationTable/OfferInformationTable.tsx";
import { OfferRulesTable } from "components/Tables/OfferRulesTable/OfferRulesTable.tsx";
import { SimpleCommissionsTableContainer } from "components/Tables/SimpleCommissionsTable/SimpleCommissionsTableContainer.tsx";
import { ActionTitle } from "components/UI/ActionTitle/ActionTitle";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { ExpandButton } from "components/UI/Table/UI/ExpandButton/ExpandButton.tsx";
import {usePaldockTranslation} from "hooks/usePaldockTranslation.tsx";
import { useUser } from "hooks/useUser.ts";
import { COMMISSIONS_PRIORITY_SORT } from "localConstants.ts";
import { ReactElement, useRef } from "react";
import "react-quill/dist/quill.snow.css";
import { CommissionType, PermissionEnum } from "types/enums.ts";

import OfferData = App.Data.OfferData;

const isHtmlEmpty = (html: string | null | undefined): boolean => {
    if (!html) return true;
    // Strip HTML tags and decode common entities, then check if any visible content remains
    const textContent = html
        .replace(/<[^>]*>/g, "") // Remove HTML tags
        .replace(/&nbsp;/gi, " ") // Replace &nbsp; with space
        .trim();
    return textContent.length === 0;
};

export const OfferDetail = ({ offer, children, childrenClassName }: { offer: OfferData; children: ReactElement; childrenClassName?: string; }) => {
    const { t } = usePaldockTranslation("components.offer-detail");
    const expandButtonRef = useRef<() => void>(null);
    const user = useUser();

    // Fetch data to check if sections should be shown
    // Mirrors SimpleCommissionsTableContainer's conditional logic
    const { data: commissionsData } = useGetCommissionsQuery("commissions", {
        page: { number: 1, size: 1 },
        filter: {
            filter: {
                "offer.id": offer.id,
                is_enabled: true,
                commission_type: user.isAffiliate() ? CommissionType.User : CommissionType.Tenant,
            },
            sort: COMMISSIONS_PRIORITY_SORT,
        },
    });

    const { data: informationData } = useGetOfferCustomFields(offer.id, {
        filter: { filter: { type: "information" } },
        page: { number: 1, size: 1 },
    });

    const { data: rulesData } = useGetOfferCustomFields(offer.id, {
        filter: { filter: { type: "rule" } },
        page: { number: 1, size: 1 },
    });

    const hasCommissions = (commissionsData?.commissions?.length ?? 0) > 0;
    const hasInformation = (informationData?.offerCustomFields?.length ?? 0) > 0;
    const hasRules = (rulesData?.offerCustomFields?.length ?? 0) > 0;
    const hasDescription = !isHtmlEmpty(offer.description);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground className={childrenClassName}>
                    {children}
                </SectionContentWrapper>
                {hasDescription && (
                    <SectionContentWrapper withBackground className={"h-[900px] overflow-hidden flex flex-col"}>
                        <Title level={3} className="!mb-6">
                            {t("description")}
                        </Title>
                        {/* ql-snow must be an ANCESTOR of ql-editor (not the same element) — Quill's
                            heading font-size rules are scoped as `.ql-snow .ql-editor h1` etc. */}
                        <div className="ql-snow overflow-y-auto">
                            {/* TODO: offer.description is rendered unsanitized (pre-existing). Should be
                                allowlist-sanitized server-side on save; ReactQuill's editor is not a
                                security boundary. */}
                            <div
                                className="ql-editor !p-0"
                                dangerouslySetInnerHTML={{ __html: offer.description ?? "" }}
                            />
                        </div>
                    </SectionContentWrapper>
                )}
            </div>

            <div className="flex flex-col gap-y-6">
                {hasCommissions && (
                    <SectionContentWrapper withBackground className={"flex flex-col"}>
                        <div className="flex justify-between items-center !mb-4">
                            <Title level={3} className="!mb-0">
                                {t("commission")}
                            </Title>
                            <ExpandButton expandButtonRef={expandButtonRef} />
                        </div>
                        <SimpleCommissionsTableContainer
                            offer={offer}
                            editPermission={PermissionEnum.CommissionUpdate}
                            showOnlyEnabledCommissions={true}
                            allowAddCommission={false}
                            expandButtonRef={expandButtonRef}
                            scrollY={297}
                            readOnlyPayout
                        />
                    </SectionContentWrapper>
                )}
                {hasInformation && (
                    <SectionContentWrapper withBackground className={"h-[380px] overflow-hidden flex flex-col"}>
                        <ActionTitle className={"mb-0"} title={{ text: t("information") }} />
                        <div className={"overflow-y-auto"}>
                            <OfferInformationTable offerId={offer.id} readOnly={true} />
                        </div>
                    </SectionContentWrapper>
                )}
                {hasRules && (
                    <SectionContentWrapper withBackground className={"h-[380px] overflow-hidden flex flex-col"}>
                        <ActionTitle className={"mb-0"} title={{ text: t("rule") }} />
                        <div className={"overflow-y-auto"}>
                            <OfferRulesTable offerId={offer.id} readOnly={true} />
                        </div>
                    </SectionContentWrapper>
                )}
            </div>
        </div>
    );
};
